import { SortDirection } from 'constants/SortDirection';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { get } from 'stores/_services/dataService';
import BaseListStore from 'stores/BaseListStore';
import ExtensionVersionStore from 'stores/extensionVersion/ExtensionVersionStore';
import { IDataFetchOptions } from '../../types';

export interface IGetExtensionVersions extends IDataFetchOptions {
    extensionId: string;
}

export default class ExtensionVersionListStore extends BaseListStore<ExtensionVersionStore> {
    extensionVersionData: Record<string, { ids: string[] }> = {};

    constructor() {
        super({
            sortBy: 'createdOn',
            sortDirection: SortDirection.DESC,
            itemStore: ExtensionVersionStore
        });

        makeObservable<ExtensionVersionListStore, '_updateExtensionVersionIds'>(this, {
            extensionVersionData: observable,
            _updateExtensionVersionIds: action
        });
    }

    private _updateExtensionVersionIds(extensionId: string, data: any, skip: number) {
        const arr = this.extensionVersionData[extensionId].ids;
        arr.length = Math.max(arr.length, data.length + skip);

        for (let i = 0; i < data.length; i++) {
            if (data[i]) {
                arr[i + skip] = data[i].id;
            }
        }
    }

    findVersionsForExtension(extensionId: string): ExtensionVersionStore[] {
        return this.extensionVersionData[extensionId]?.ids.map((id) => this.data.get(id)) || [];
    }

    async getExtensionVersions({ extensionId, ...args }: IGetExtensionVersions): Promise<void> {
        const shouldReset = this._updateListState(args);

        const options = {
            filter: {
                order: [this.sort, `sha256 ${this.sortDirection}`],
                include: [
                    {
                        relation: 'author',
                        scope: {
                            fields: ['email']
                        }
                    }
                ]
            }
        };

        const { data } = await get(`extensions/${extensionId}/versions`, options);

        if (shouldReset) {
            this.clearIds();
            //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
            runInAction(() => {
                this.extensionVersionData[extensionId] = { ids: [] };
            });
        }

        if (!this.extensionVersionData[extensionId]) {
            runInAction(() => {
                this.extensionVersionData[extensionId] = { ids: [] };
            });
        }

        this._updateData(data);
        this._updateExtensionVersionIds(extensionId, data, this.skip);
    }

    async getExtensionVersion(extensionVersionId: string): Promise<void> {
        const options = {
            filter: {
                include: [
                    {
                        relation: 'author',
                        scope: {
                            fields: ['email']
                        }
                    }
                ]
            }
        };

        const { data } = await get(`extensionVersions/${extensionVersionId}/detail`, options);

        this._updateData(data);
    }
}
